import { FormEvent, ReactNode, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { useAuth } from "../store/auth";
import PageHeader from "../components/ui/PageHeader";
import Avatar from "../components/ui/Avatar";
import { TIMEZONE_GROUPS } from "../lib/timezones";
import {
  IconAlert,
  IconCalendar,
  IconCheck,
  IconCrown,
  IconGlobe,
  IconLock,
  IconSettings,
  IconShield,
} from "../components/ui/Icons";

export default function Settings() {
  const { t, i18n } = useTranslation();
  const user = useAuth((s) => s.user);
  const setUser = useAuth((s) => s.setUser);
  const qc = useQueryClient();

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await api.get("/api/auth/me");
      return data;
    },
  });

  const locale = i18n.resolvedLanguage || i18n.language || "en";
  const displayName = user?.name || user?.email || "?";

  const [timezone, setTimezone] = useState<string>("");
  // Sync select value once /me data arrives
  const currentTz = meQuery.data?.timezone ?? "UTC";

  const tzMutation = useMutation({
    mutationFn: async (tz: string) => {
      const { data } = await api.patch("/api/auth/profile", { timezone: tz });
      return data;
    },
    onSuccess: (data) => {
      setUser({ ...user!, timezone: data.timezone } as typeof user & { timezone: string });
      void qc.invalidateQueries({ queryKey: ["me"] });
      setTimezone("");
      toast.success(t("settings.account.timezoneSaved"));
    },
    onError: (err) => toast.error(apiError(err)),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.settings")}
        title={t("settings.title")}
        subtitle={t("settings.subtitle")}
        icon={<IconSettings />}
      />

      {/* Account card */}
      <section className="card">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
          <Avatar name={displayName} size="lg" />
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <p className="section-eyebrow">{t("settings.account.heading")}</p>
              <h2 className="mt-0.5 truncate text-xl font-semibold text-white">
                {user?.name || t("common.dash")}
              </h2>
              <p className="truncate text-sm text-slate-400">{user?.email}</p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {(meQuery.data?.plan ?? user?.plan) && (
                <Field icon={<IconCrown size={14} />} label={t("settings.account.planLabel")}>
                  <span className="inline-flex items-center gap-2">
                    <span className="font-medium capitalize text-white">
                      {meQuery.data?.plan ?? user?.plan}
                    </span>
                    <span className="pill bg-brand-500/15 text-brand-200 ring-1 ring-brand-400/30">
                      {t("settings.plan.currentBadge")}
                    </span>
                  </span>
                </Field>
              )}
              <Field icon={<IconGlobe size={14} />} label={t("settings.account.timezoneLabel")}>
                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const val = timezone || currentTz;
                    if (val !== currentTz || timezone) tzMutation.mutate(val);
                  }}
                >
                  <select
                    className="flex-1 rounded-lg border border-slate-700 bg-slate-800 px-2 py-1 text-sm text-white focus:border-brand-500 focus:outline-none"
                    value={timezone || currentTz}
                    onChange={(e) => setTimezone(e.target.value)}
                  >
                    {TIMEZONE_GROUPS.map((g) => (
                      <optgroup key={g.region} label={g.region}>
                        {g.zones.map((z) => (
                          <option key={z.id} value={z.id}>{z.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <button
                    type="submit"
                    disabled={tzMutation.isPending || (!timezone || timezone === currentTz)}
                    className="rounded-lg bg-brand-600 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-40"
                  >
                    {tzMutation.isPending ? "…" : t("common.save")}
                  </button>
                </form>
              </Field>
              {meQuery.data?.createdAt && (
                <Field
                  icon={<IconCalendar size={14} />}
                  label={t("settings.account.memberSinceLabel")}
                >
                  {new Date(meQuery.data.createdAt).toLocaleDateString(locale, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Field>
              )}
            </div>
          </div>
        </div>
      </section>

      <BillingCard />

      <ChangePasswordCard />

      {/* Risk awareness card */}
      <section className="card">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/30">
            <IconAlert size={18} />
          </span>
          <div className="min-w-0 space-y-2">
            <h3 className="text-base font-semibold text-white">{t("settings.risk.heading")}</h3>
            <p className="text-sm text-slate-400">{t("settings.risk.body")}</p>
            <p className="text-sm text-slate-400">
              <Trans i18nKey="settings.risk.bodyChallenge" components={{ strong: <strong className="text-white" /> }} />
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

interface SubscriptionInfo {
  plan: "solo" | "pro" | "label" | null;
  status: string | null;
  hasActiveSubscription: boolean;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  trialDays: number;
}

function BillingCard(): JSX.Element {
  const { t, i18n } = useTranslation();
  const subQuery = useQuery({
    queryKey: ["subscription"],
    queryFn: async (): Promise<SubscriptionInfo> => {
      const { data } = await api.get("/api/billing/subscription");
      return data;
    },
  });

  const portalMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/api/billing/portal");
      return data as { url: string };
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const checkoutMutation = useMutation({
    mutationFn: async (plan: "solo" | "pro" | "label") => {
      const { data } = await api.post("/api/billing/checkout", { plan });
      return data as { url: string };
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const sub = subQuery.data;
  const locale = i18n.resolvedLanguage || i18n.language || "en";

  const status = sub?.status;
  const isTrialing = status === "trialing";
  const isPastDue = status === "past_due";
  const isCanceled = status === "canceled" || status === "incomplete_expired" || status === "unpaid";
  const isActive = sub?.hasActiveSubscription;

  const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <section className="card-feature">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <p className="section-eyebrow">{t("settings.plan.heading")}</p>
            <h3 className="text-lg font-semibold capitalize text-white">
              {sub?.plan ? t(`landing.pricing.plans.${sub.plan}.name`) : t("settings.plan.none")}
            </h3>
            {status && (
              <p className="text-sm text-slate-400">
                {t(`settings.plan.status.${status}`, t("settings.plan.status.unknown"))}
              </p>
            )}
          </div>
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-500/20 text-brand-200 ring-1 ring-brand-400/30">
            <IconCrown />
          </span>
        </div>

        {isTrialing && sub?.trialEndsAt && (
          <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200 ring-1 ring-emerald-400/30">
            {t("settings.plan.trialBanner", { date: formatDate(sub.trialEndsAt) })}
          </p>
        )}
        {isPastDue && (
          <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-200 ring-1 ring-amber-400/30">
            {t("settings.plan.pastDueBanner")}
          </p>
        )}
        {sub?.cancelAtPeriodEnd && sub?.currentPeriodEnd && (
          <p className="rounded-lg bg-slate-500/10 px-3 py-2 text-sm text-slate-200 ring-1 ring-slate-500/30">
            {t("settings.plan.cancelAtPeriodEndBanner", {
              date: formatDate(sub.currentPeriodEnd),
            })}
          </p>
        )}
        {!isTrialing && !sub?.cancelAtPeriodEnd && sub?.currentPeriodEnd && status === "active" && (
          <p className="text-sm text-slate-400">
            {t("settings.plan.renewsOn", { date: formatDate(sub.currentPeriodEnd) })}
          </p>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {isActive ? (
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
            >
              {portalMutation.isPending
                ? t("settings.plan.opening")
                : t("settings.plan.manage")}
            </button>
          ) : (
            <>
              {(["solo", "pro", "label"] as const).map((plan) => (
                <button
                  key={plan}
                  type="button"
                  className={plan === "pro" ? "btn-primary btn-sm" : "btn-secondary btn-sm"}
                  onClick={() => checkoutMutation.mutate(plan)}
                  disabled={checkoutMutation.isPending}
                >
                  {t(`landing.pricing.plans.${plan}.cta`)}
                </button>
              ))}
            </>
          )}
        </div>

        {isCanceled && (
          <p className="text-xs text-slate-500">
            {t("settings.plan.canceledHelp")}
          </p>
        )}
      </div>
    </section>
  );
}

function Field({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-800/70 bg-slate-950/40 p-3">
      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        <span className="text-slate-400">{icon}</span>
        {label}
      </div>
      <div className="text-sm text-slate-100">{children}</div>
    </div>
  );
}

function ChangePasswordCard(): JSX.Element {
  const { t } = useTranslation();
  const setSession = useAuth((s) => s.setSession);
  const user = useAuth((s) => s.user);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (payload: { currentPassword: string; newPassword: string }) => {
      const { data } = await api.post("/api/auth/change-password", payload);
      return data as { ok: true; token: string };
    },
    onSuccess: (data) => {
      if (user) setSession(data.token, user);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setLocalError(null);
      toast.success(t("settings.password.toast.changed"));
    },
    onError: (err) => {
      toast.error(apiError(err));
    },
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setLocalError(null);

    if (newPassword.length < 8) {
      setLocalError(t("settings.password.errors.tooShort"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError(t("settings.password.errors.mismatch"));
      return;
    }
    if (newPassword === currentPassword) {
      setLocalError(t("settings.password.errors.sameAsCurrent"));
      return;
    }

    mutation.mutate({ currentPassword, newPassword });
  }

  const passwordOk = newPassword.length >= 8;
  const matchOk = passwordOk && newPassword === confirmPassword;

  return (
    <form onSubmit={onSubmit} className="card space-y-5">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-500/15 text-brand-200 ring-1 ring-brand-400/30">
          <IconLock size={18} />
        </span>
        <div>
          <h3 className="text-base font-semibold text-white">{t("settings.password.heading")}</h3>
          <p className="mt-0.5 text-sm text-slate-400">{t("settings.password.help")}</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="label" htmlFor="current-password">
            {t("settings.password.current")}
          </label>
          <input
            id="current-password"
            type="password"
            required
            autoComplete="current-password"
            className="input"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>

        <div>
          <label className="label" htmlFor="new-password">
            {t("settings.password.new")}
          </label>
          <input
            id="new-password"
            type="password"
            required
            minLength={8}
            maxLength={128}
            autoComplete="new-password"
            className="input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>

        <div>
          <label className="label" htmlFor="confirm-password">
            {t("settings.password.confirm")}
          </label>
          <input
            id="confirm-password"
            type="password"
            required
            minLength={8}
            maxLength={128}
            autoComplete="new-password"
            className="input"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>
      </div>

      {/* Live password requirements */}
      <ul className="space-y-1 text-xs">
        <Requirement met={passwordOk}>{t("settings.password.hint")}</Requirement>
        <Requirement met={matchOk}>{t("settings.password.confirm")}</Requirement>
      </ul>

      {localError && (
        <p className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200" role="alert">
          {localError}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          type="submit"
          className="btn-primary w-full sm:w-auto"
          disabled={mutation.isPending}
        >
          <IconShield size={16} />
          {mutation.isPending ? t("settings.password.submitting") : t("settings.password.submit")}
        </button>
      </div>
    </form>
  );
}

function Requirement({ met, children }: { met: boolean; children: ReactNode }): JSX.Element {
  return (
    <li className="flex items-center gap-2">
      <span
        className={
          met
            ? "inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300"
            : "inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-800 text-slate-500"
        }
      >
        <IconCheck size={10} strokeWidth={3} />
      </span>
      <span className={met ? "text-slate-300" : "text-slate-500"}>{children}</span>
    </li>
  );
}
